import React, { useEffect, createElement } from 'react';
import { Star, Clock, Frown, Repeat, Brain, AlertTriangle, CheckCircle2, Heart, Gift, Sparkles, Lock, Infinity as InfinityIcon } from 'lucide-react';
import { PurchaseButton } from './components/PurchaseButton';
import { CountdownTimer } from './components/CountdownTimer';
import { SocialProofPopup } from './components/SocialProofPopup';
import { AccordionItem } from './components/Accordion';
export function App() {
  // Initialize Meta Pixel
  useEffect(() => {
    const pixelId = '2782582688765590';
    const w = window as any;
    // Set up the fbq queue stub (safe to re-run; guarded below)
    if (!w.fbq) {
      const n: any = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      w.fbq = n;
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      // Inject the Facebook events script, appending to <head> safely
      // instead of relying on an existing <script> tag being present.
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const target = document.head || document.body;
      if (target) {
        target.appendChild(script);
      }
    }
    w.fbq('init', pixelId);
    w.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-brand-babypink font-sans text-brand-black selection:bg-brand-pink selection:text-white pb-20" data-id="element-0">
      <SocialProofPopup data-id="element-1" />

      {/* HERO SECTION */}
      <header className="pt-12 pb-16 px-4 max-w-4xl mx-auto text-center" data-id="element-2">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-brand-black mb-8 leading-tight" data-id="element-3">
          Deja de improvisar tus clases de barre
        </h1>

        <a href="https://checkout.ebookbtnlines.shop/cart/45487498461231:1" className="block relative w-full max-w-2xl mx-auto mb-8 rounded-2xl overflow-hidden shadow-soft transition-transform duration-300 hover:scale-[1.02] cursor-pointer" data-id="element-4">
          <img src="https://cdn.magicpatterns.com/uploads/x2CBHN4wbevecgoL629dDW/imagen_sin_fondo.png" alt="Barre Resuelto Bundle" className="w-full h-auto object-cover bg-white" data-id="element-5" />
        </a>

        <h2 className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-6 leading-relaxed" data-id="element-6">
          Todo lo que necesitás para crear clases de barre más organizadas,
          creativas y seguras, sin perder horas planificando.
        </h2>

        <div className="flex items-center justify-center gap-2 mb-8" data-id="element-7">
          <div className="flex text-yellow-400" data-id="element-8">
            {[...Array(5)].map((_, i) => <Star key={i} size={20} fill="currentColor" data-id="element-9" />)}
          </div>
          <span className="font-semibold text-gray-700" data-id="element-10">4.9/5 estrellas</span>
        </div>

        <div className="flex flex-col items-center" data-id="element-11">
          <PurchaseButton data-id="element-12" />
          <p className="text-brand-pink font-bold mt-4 uppercase tracking-wide text-sm" data-id="element-13">
            Oferta especial solo por hoy
          </p>
        </div>
      </header>

      {/* PAIN POINTS SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-14">
        <div className="max-w-5xl mx-auto" data-id="element-15">
          <div className="text-center mb-12" data-id="element-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-17">
              ¿Te suena familiar esta situación?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-18">
              Sabemos lo frustrante que es llegar a una clase de barre sin tener
              claro qué ejercicios hacer, sintiendo que siempre terminás
              improvisando y repitiendo lo mismo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-19">
            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center" data-id="element-20">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-21">
                <Clock size={32} data-id="element-22" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-23">
                Pérdida de Tiempo
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-24">
                Pasás horas buscando inspiración en Instagram, Pinterest o
                YouTube, armando clases a último momento en lugar de descansar,
                disfrutar tu tiempo libre o dedicarte a hacer crecer tu negocio.
              </p>
            </div>

            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center" data-id="element-25">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-26">
                <Frown size={32} data-id="element-27" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-28">
                Estrés e Improvisación
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-29">
                Llegás a la clase sin una estructura clara, dudando qué
                ejercicio sigue y sintiendo que todo depende de improvisar sobre
                la marcha.
              </p>
            </div>

            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center" data-id="element-30">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-31">
                <Repeat size={32} data-id="element-32" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-33">
                Falta de Variedad
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-34">
                Terminás utilizando siempre los mismos movimientos y secuencias,
                con miedo a que tus alumnas se aburran o sientan que todas las
                clases son iguales.
              </p>
            </div>

            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center md:col-span-2 lg:col-span-1" data-id="element-35">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-36">
                <Brain size={32} data-id="element-37" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-38">
                Agotamiento Mental
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-39">
                Más allá de enseñar, tenés que pensar constantemente qué hacer,
                cómo combinar ejercicios y cómo adaptar cada clase. Esa carga
                mental termina agotándote.
              </p>
            </div>

            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center md:col-span-2 lg:col-span-1 lg:col-start-2" data-id="element-40">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-41">
                <AlertTriangle size={32} data-id="element-42" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-43">
                Menos Confianza
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-44">
                Cuando no tenés un sistema, es fácil sentir inseguridad y
                preguntarte si realmente estás ofreciendo la mejor experiencia
                posible a tus alumnas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="py-16 px-4 bg-brand-lilac/30" data-id="element-45">
        <div className="max-w-5xl mx-auto" data-id="element-46">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-12 text-center" data-id="element-47">
            Lo que vas a lograr con Barre Resuelto
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-48">
            {[{
            title: 'Ahorrá Horas de Planificación',
            desc: 'Dejá de pasar horas buscando ideas en Instagram o Pinterest. Vas a tener clases organizadas y listas para aplicar.'
          }, {
            title: 'Clases Más Creativas y Variadas',
            desc: 'Aprendé a combinar ejercicios y crear secuencias diferentes para que ninguna clase vuelva a sentirse repetitiva.'
          }, {
            title: 'Más Seguridad al Enseñar',
            desc: 'Entrá a cada clase sabiendo exactamente qué hacer y cómo hacer que todo fluya de principio a fin.'
          }, {
            title: 'Alumnas Más Motivadas',
            desc: 'Diseñá clases dinámicas y entretenidas que hagan que tus alumnas disfruten cada sesión y quieran volver.'
          }, {
            title: 'Menos Estrés, Más Disfrute',
            desc: 'Dejá atrás la improvisación y volvé a disfrutar enseñar, enfocándote en lo que más te gusta.'
          }].map((benefit, idx) => <div key={idx} className={`bg-white p-6 rounded-2xl shadow-soft flex items-start gap-4 ${idx === 4 ? 'md:col-span-2 md:max-w-md md:mx-auto' : ''}`} data-id="element-49">
                <div className="bg-brand-babypink p-2 rounded-full flex-shrink-0 mt-1" data-id="element-50">
                  <CheckCircle2 size={24} className="text-brand-pink" data-id="element-51" />
                </div>
                <div data-id="element-52">
                  <h3 className="font-bold text-lg mb-2" data-id="element-53">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm" data-id="element-54">{benefit.desc}</p>
                </div>
              </div>)}
          </div>

          <div className="mt-12 flex justify-center" data-id="element-55">
            <PurchaseButton data-id="element-56" />
          </div>
        </div>
      </section>

      {/* CURRICULUM SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-57">
        <div className="max-w-3xl mx-auto" data-id="element-58">
          <div className="text-center mb-10" data-id="element-59">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-60">
              BARRE RESUELTO
            </h2>
            <p className="text-xl text-brand-pink font-medium" data-id="element-61">
              📚 Formación completa para profes de Barre
            </p>
          </div>

          <div className="space-y-2" data-id="element-62">
            <AccordionItem title="🧱 TOMO 1 — Tu clase de barre de principio a fin: la estructura que necesitás" data-id="element-63">
              <ul className="space-y-3" data-id="element-64">
                <li className="flex items-start gap-2" data-id="element-65">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-66" />{' '}
                  Por qué la mayoría de las clases de barre se sienten
                  improvisadas
                </li>
                <li className="flex items-start gap-2" data-id="element-67">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-68" />{' '}
                  La estructura base de una clase de 55 minutos
                </li>
                <li className="flex items-start gap-2" data-id="element-69">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-70" />{' '}
                  El calentamiento
                </li>
                <li className="flex items-start gap-2" data-id="element-71">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-72" />{' '}
                  El trabajo en barre
                </li>
                <li className="flex items-start gap-2" data-id="element-73">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-74" />{' '}
                  Centro, piso y cierre
                </li>
                <li className="flex items-start gap-2" data-id="element-75">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-76" />{' '}
                  Cómo hacer que una clase fluya naturalmente
                </li>
                <li className="flex items-start gap-2" data-id="element-77">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-78" />{' '}
                  Errores comunes de estructura
                </li>
                <li className="flex items-start gap-2" data-id="element-79">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-80" />{' '}
                  Cómo adaptar la intensidad según el grupo
                </li>
                <li className="flex items-start gap-2" data-id="element-81">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-82" />{' '}
                  Qué hacer cuando no sabés qué sigue
                </li>
                <li className="flex items-start gap-2" data-id="element-83">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-84" />{' '}
                  Clase modelo completa
                </li>
              </ul>
            </AccordionItem>

            <AccordionItem title="💪 TOMO 2 — Biblioteca de ejercicios y variaciones" data-id="element-85">
              <ul className="space-y-3" data-id="element-86">
                <li className="flex items-start gap-2" data-id="element-87">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-88" />{' '}
                  Ejercicios para piernas
                </li>
                <li className="flex items-start gap-2" data-id="element-89">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-90" />{' '}
                  Ejercicios para glúteos
                </li>
                <li className="flex items-start gap-2" data-id="element-91">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-92" />{' '}
                  Ejercicios para brazos
                </li>
                <li className="flex items-start gap-2" data-id="element-93">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-94" />{' '}
                  Ejercicios para abdomen
                </li>
                <li className="flex items-start gap-2" data-id="element-95">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-96" />{' '}
                  Ejercicios para espalda y postura
                </li>
                <li className="flex items-start gap-2" data-id="element-97">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-98" />{' '}
                  Variaciones para principiantes
                </li>
                <li className="flex items-start gap-2" data-id="element-99">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-100" />{' '}
                  Variaciones para alumnas avanzadas
                </li>
                <li className="flex items-start gap-2" data-id="element-101">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-102" />{' '}
                  Cómo aumentar o disminuir la intensidad
                </li>
                <li className="flex items-start gap-2" data-id="element-103">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-104" />{' '}
                  Combinaciones de ejercicios
                </li>
                <li className="flex items-start gap-2" data-id="element-105">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-106" />{' '}
                  Progresiones simples
                </li>
              </ul>
            </AccordionItem>

            <AccordionItem title="🎨 TOMO 3 — Cómo crear clases creativas y diferentes" data-id="element-107">
              <ul className="space-y-3" data-id="element-108">
                <li className="flex items-start gap-2" data-id="element-109">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-110" />{' '}
                  Cómo dejar de repetir siempre lo mismo
                </li>
                <li className="flex items-start gap-2" data-id="element-111">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-112" />{' '}
                  Métodos para combinar ejercicios
                </li>
                <li className="flex items-start gap-2" data-id="element-113">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-114" />{' '}
                  Secuencias que funcionan
                </li>
                <li className="flex items-start gap-2" data-id="element-115">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-116" />{' '}
                  Cómo crear bloques dinámicos
                </li>
                <li className="flex items-start gap-2" data-id="element-117">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-118" />{' '}
                  Clases temáticas
                </li>
                <li className="flex items-start gap-2" data-id="element-119">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-120" />{' '}
                  Cómo mantener motivadas a tus alumnas
                </li>
                <li className="flex items-start gap-2" data-id="element-121">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-122" />{' '}
                  Ideas para variar las clases
                </li>
                <li className="flex items-start gap-2" data-id="element-123">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-124" />{' '}
                  Cómo hacer transiciones fluidas
                </li>
                <li className="flex items-start gap-2" data-id="element-125">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-126" />{' '}
                  Ritmo y energía de la clase
                </li>
                <li className="flex items-start gap-2" data-id="element-127">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-128" />{' '}
                  Clase modelo creativa
                </li>
              </ul>
            </AccordionItem>

            <AccordionItem title="🤸‍♀️ TOMO 4 — Adaptaciones y modificaciones" data-id="element-129">
              <ul className="space-y-3" data-id="element-130">
                <li className="flex items-start gap-2" data-id="element-131">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-132" />{' '}
                  Cómo trabajar con alumnas principiantes
                </li>
                <li className="flex items-start gap-2" data-id="element-133">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-134" />{' '}
                  Adaptaciones para distintos niveles
                </li>
                <li className="flex items-start gap-2" data-id="element-135">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-136" />{' '}
                  Cómo corregir sin interrumpir la clase
                </li>
                <li className="flex items-start gap-2" data-id="element-137">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-138" />{' '}
                  Opciones de menor impacto
                </li>
                <li className="flex items-start gap-2" data-id="element-139">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-140" />{' '}
                  Opciones de mayor intensidad
                </li>
                <li className="flex items-start gap-2" data-id="element-141">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-142" />{' '}
                  Cómo dar alternativas durante una misma clase
                </li>
                <li className="flex items-start gap-2" data-id="element-143">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-144" />{' '}
                  Errores frecuentes
                </li>
                <li className="flex items-start gap-2" data-id="element-145">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-146" />{' '}
                  Cómo manejar grupos con distintos niveles
                </li>
                <li className="flex items-start gap-2" data-id="element-147">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-148" />{' '}
                  Indicaciones claras y efectivas
                </li>
                <li className="flex items-start gap-2" data-id="element-149">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-150" />{' '}
                  Clase modelo adaptable
                </li>
              </ul>
            </AccordionItem>

            <AccordionItem title="🗓️ TOMO 5 — Planificación y organización para profes" data-id="element-151">
              <ul className="space-y-3" data-id="element-152">
                <li className="flex items-start gap-2" data-id="element-153">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-154" />{' '}
                  Cómo planificar sin perder horas
                </li>
                <li className="flex items-start gap-2" data-id="element-155">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-156" />{' '}
                  Planificación semanal
                </li>
                <li className="flex items-start gap-2" data-id="element-157">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-158" />{' '}
                  Planificación mensual
                </li>
                <li className="flex items-start gap-2" data-id="element-159">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-160" />{' '}
                  Cómo evitar la improvisación
                </li>
                <li className="flex items-start gap-2" data-id="element-161">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-162" />{' '}
                  Cómo organizar tus ideas
                </li>
                <li className="flex items-start gap-2" data-id="element-163">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-164" />{' '}
                  Qué hacer cuando te quedás sin inspiración
                </li>
                <li className="flex items-start gap-2" data-id="element-165">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-166" />{' '}
                  Cómo ahorrar tiempo preparando clases
                </li>
                <li className="flex items-start gap-2" data-id="element-167">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-168" />{' '}
                  Organización para profes ocupadas
                </li>
                <li className="flex items-start gap-2" data-id="element-169">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-170" />{' '}
                  Calendario de clases
                </li>
                <li className="flex items-start gap-2" data-id="element-171">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-172" />{' '}
                  Sistema de planificación editable
                </li>
              </ul>
            </AccordionItem>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-16 px-4 bg-brand-babypink" data-id="element-173">
        <div className="max-w-6xl mx-auto" data-id="element-174">
          <div className="text-center mb-12" data-id="element-175">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-176">
              Profes que ya llevaron sus clases al siguiente nivel
            </h2>
            <p className="text-xl text-gray-600" data-id="element-177">
              Más de 500 profes de Argentina ya usan nuestro sistema
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-178">
            {[{
            name: 'Micaela R.',
            role: 'Profe de Barre hace 2 años',
            text: 'Antes pasaba mis domingos enteros armando clases. Ahora con este sistema armo la semana en 30 minutos. Mis alumnas notaron el cambio de energía porque yo estoy más relajada.'
          }, {
            name: 'Julieta G.',
            role: 'Instructora de Pilates y Barre',
            text: 'Me salvó la vida. Siempre sentía que repetía los mismos 5 ejercicios de glúteos. La biblioteca de variaciones es oro puro, mis clases ahora son súper dinámicas.'
          }, {
            name: 'Carolina M.',
            role: 'Profe principiante',
            text: 'Recién arranco a dar clases y me daba pánico quedarme en blanco. Tener la estructura clara del Tomo 1 me dio muchísima seguridad para pararme frente al grupo.'
          }, {
            name: 'Luciana V.',
            role: 'Dueña de estudio',
            text: 'Compré el material para unificar el criterio de las profes de mi estudio. Es excelente. Nos organizó a todas y la calidad de las clases subió un 100%.'
          }, {
            name: 'Paula S.',
            role: 'Profe de Barre',
            text: 'Lo que más me costaba era adaptar los ejercicios para las chicas nuevas sin frenar la clase. El módulo de adaptaciones me dio las herramientas exactas que necesitaba.'
          }].map((testimonial, idx) => <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm border border-brand-pastelpink" data-id="element-179">
                <div className="flex text-yellow-400 mb-4" data-id="element-180">
                  {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-181" />)}
                </div>
                <p className="text-gray-700 text-sm italic mb-4" data-id="element-182">
                  "{testimonial.text}"
                </p>
                <div data-id="element-183">
                  <p className="font-bold text-brand-black" data-id="element-184">
                    {testimonial.name}
                  </p>
                  <p className="text-xs text-brand-pink" data-id="element-185">{testimonial.role}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-4 bg-brand-lilac/20" data-id="element-186">
        <div className="max-w-3xl mx-auto" data-id="element-187">
          <div className="text-center mb-10" data-id="element-188">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-brand-pink" data-id="element-189">
              Oferta por tiempo limitado
            </h2>
            <p className="text-xl font-medium" data-id="element-190">
              Llevate estos BONOS GRATIS hoy
            </p>
          </div>

          <div className="space-y-6" data-id="element-191">
            {[{
            title: '🎁 Bono 1 — 100 ejercicios de barre',
            desc: 'Biblioteca completa de ejercicios para todo el cuerpo.',
            image: 'https://cdn.magicpatterns.com/uploads/bEBJUJG1PWSSetB3vLggx7/WhatsApp_Image_2026-06-19_at_7.48.49_PM.jpg'
          }, {
            title: '🎁 Bono 2 — 30 clases listas para usar',
            desc: 'Clases completas con estructura, duración y variantes.',
            image: 'https://cdn.magicpatterns.com/uploads/e2QFUqjhhnqJTF7jekEbUt/WhatsApp_Image_2026-06-19_at_7.48.52_PM.jpg'
          }, {
            title: '🎁 Bono 3 — Secuencias y combinaciones listas',
            desc: 'Bloques de ejercicios para crear clases más dinámicas.',
            image: 'https://cdn.magicpatterns.com/uploads/f242W92Cq6FJSxN1RgpkDT/WhatsApp_Image_2026-06-19_at_7.48.54_PM.jpg'
          }, {
            title: '🎁 Bono 4 — Guía para profes sin inspiración',
            desc: 'Qué hacer cuando sentís que repetís siempre lo mismo.',
            image: 'https://cdn.magicpatterns.com/uploads/69fKL2fqxWvRkuEmqbBeVo/WhatsApp_Image_2026-06-19_at_7.48.56_PM.jpg'
          }, {
            title: '🎁 Bono 5 — Atlas visual de ejercicios',
            desc: 'Fichas visuales con posiciones, variantes y objetivos.',
            image: 'https://cdn.magicpatterns.com/uploads/uQSJQGBr9aaFdKnhSyba7X/WhatsApp_Image_2026-06-19_at_7.48.59_PM.jpg'
          }, {
            title: '🎁 Bono 6 — Planillas de planificación para profes',
            desc: 'Plantillas editables para organizar semanas y meses.',
            image: 'https://cdn.magicpatterns.com/uploads/e2QFUqjhhnqJTF7jekEbUt/WhatsApp_Image_2026-06-19_at_7.48.52_PM.jpg'
          }].map((bono, idx) => <div key={idx} className="bg-white rounded-2xl shadow-soft border border-brand-pastelpink overflow-hidden" data-id="element-192">
                {/* PREVIEW IMAGE WITH GRADIENT */}
                <a href="https://checkout.ebookbtnlines.shop/cart/45487498461231:1" className="block relative h-44 md:h-52 overflow-hidden group" data-id="element-193">
                  <img src={bono.image} alt={bono.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" data-id="element-194" />
                  {/* Degrade overlay fading into the card */}
                  <div className="absolute inset-0 bg-gradient-to-b from-brand-pink/20 via-transparent to-white pointer-events-none" data-id="element-195" />
                  <span className="absolute top-3 left-3 bg-brand-pink text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm" data-id="element-196">
                    Vista previa
                  </span>
                </a>

                <AccordionItem title={bono.title} data-id="element-197">{bono.desc}</AccordionItem>
              </div>)}
          </div>

          <div className="mt-12 flex justify-center" data-id="element-198">
            <PurchaseButton data-id="element-199" />
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-200">
        <div className="max-w-2xl mx-auto text-center" data-id="element-201">
          <span className="inline-block bg-brand-pink text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4" data-id="element-202">
            Solo por hoy
          </span>
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3" data-id="element-203">
            Esto es todo lo que te llevás
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-204">
            Acceso inmediato y de por vida a Barre Resuelto más todos los bonos
            de regalo.
          </p>

          {/* COUNTDOWN UNDER TITLE/SUBTITLE */}
          <div className="flex flex-col items-center mb-8" data-id="element-205">
            <p className="text-brand-pink font-bold uppercase tracking-wide text-sm" data-id="element-206">
              La oferta termina en
            </p>
            <CountdownTimer data-id="element-207" />
          </div>

          {/* VALUE STACK CARD */}
          <div className="bg-brand-babypink rounded-3xl shadow-soft border border-brand-pastelpink p-6 md:p-8 text-left" data-id="element-208">
            <ul className="space-y-3" data-id="element-209">
              {[{
              icon: <Heart size={18} className="text-brand-pink" data-id="element-210" />,
              label: 'Formación completa (Tomos 1 al 5)',
              value: '$49.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-211" />,
              label: 'Bono 1 — 100 ejercicios de barre',
              value: '$9.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-212" />,
              label: 'Bono 2 — 30 clases listas para usar',
              value: '$9.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-213" />,
              label: 'Bono 3 — Secuencias y combinaciones',
              value: '$7.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-214" />,
              label: 'Bono 4 — Guía para profes sin inspiración',
              value: '$5.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-215" />,
              label: 'Bono 5 — Atlas visual de ejercicios',
              value: '$7.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-216" />,
              label: 'Bono 6 — Planillas de planificación',
              value: '$5.990'
            }, {
              icon: <InfinityIcon size={18} className="text-brand-pink" data-id="element-217" />,
              label: 'Acceso de por vida + futuras mejoras',
              value: 'Incluido'
            }].map((item, idx) => <li key={idx} className="flex items-center justify-between gap-3 border-b border-brand-pastelpink/60 pb-3 last:border-0 last:pb-0" data-id="element-218">
                  <span className="flex items-center gap-2 text-sm text-brand-black" data-id="element-219">
                    <span className="bg-white p-1.5 rounded-full flex-shrink-0" data-id="element-220">
                      {item.icon}
                    </span>
                    {item.label}
                  </span>
                  <span className="text-sm text-gray-400 line-through whitespace-nowrap" data-id="element-221">
                    {item.value}
                  </span>
                </li>)}
            </ul>

            <div className="mt-6 pt-5 border-t-2 border-dashed border-brand-pink/40 text-center" data-id="element-222">
              <p className="text-sm text-gray-500" data-id="element-223">
                Valor real total:{' '}
                <span className="line-through font-semibold" data-id="element-224">$97.920</span>
              </p>
              <div className="flex items-center justify-center gap-3 mt-1" data-id="element-225">
                <span className="text-gray-400 line-through text-lg" data-id="element-226">
                  $49.990
                </span>
                <span className="text-4xl md:text-5xl font-serif font-bold text-brand-pink" data-id="element-227">
                  $19.990
                </span>
              </div>
              <p className="inline-flex items-center gap-1 text-xs font-bold text-white bg-brand-pink rounded-full px-3 py-1 mt-3" data-id="element-228">
                <Sparkles size={14} data-id="element-229" /> Ahorrás más de $77.000 hoy
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center mt-8" data-id="element-230">
            <PurchaseButton className="scale-105 hover:scale-110 mt-2" data-id="element-231" />
            <p className="text-sm text-gray-500 mt-4 flex items-center justify-center gap-2" data-id="element-232">
              <Lock size={16} className="text-green-500" data-id="element-233" />
              Pago 100% seguro · Acceso inmediato
            </p>
          </div>
        </div>
      </section>
    </div>;
}